<template>
  <div class="create-work-form">
    <div class="content">
      <div class="form-group flex-grow pl-8 pt-8 pr-8">
        <div class="header">
          <h2>Video settings</h2>
          <button class="close-btn" @click="close()">×</button>
        </div>

        <label for="video">Video Url</label>
        <input
            type="text"
            name="video"
            class="mb-2"
            placeholder="Video File"
            :value="videoFile ? videoFile.name : videoUrl"
            disabled
        />

        <!-- Start and End time inputs -->
        <label>Adjust time frame</label>
        <div class="time flex justify-between items-center">
          <div class="start">
            <div class="item-title">
              <span>Start:</span>
              <input
                  type="text"
                  name="start"
                  :value="formatTime(sliderValue[0])"
                  :min="0"
                  :max="duration"
                  @input="restartAutoplay"
              />
            </div>
          </div>
          <div class="end">
            <div class="item-title">
              <span>End:</span>
              <input
                  type="text"
                  name="end"
                  :value="formatTime(sliderValue[1])"
                  :min="0"
                  :max="duration"
                  @input="restartAutoplay"
              />
            </div>
          </div>
        </div>

        <!-- Total Duration Display -->
        <label class="mt-3 total-duration" v-if="duration > 0">
          Total duration: {{ formatTime(sliderValue[1] - sliderValue[0]) }}
        </label>


        <client-only>
          <VueSlider
              v-if="duration > 0"
              v-model="sliderValue"
              :min="0"
              :max="duration"
              :interval="1"
              :lazy="true"
              :useKeyboard="true"
              :tooltip="'none'"
              :enableCross="false"
              :formatter="formatTime"
              :dotSize="16"
          />
        </client-only>
        <!-- Checkboxes for Muted and Loop -->
        <div class="mt-4" v-if="false">

          <div class="flex justify-start items-center">
            <span style="min-width: 100px">Muted</span>
            <input type="checkbox" v-model="muted"/>
          </div>
          <div class="flex justify-start items-center">
            <span style="min-width: 100px">Loop</span>
            <input type="checkbox" v-model="loop"/>
          </div>
        </div>

      </div>

      <!-- Video preview -->
      <div class="form-group flex-grow">
        <video
            v-if="videoSrc"
            :src="videoSrc"
            ref="videoElement"
            controls
            autoplay
            class="video-preview"
            muted
            :loop="loop"
            @timeupdate="checkVideoTime"
            @play="ensureMuted"
        />
        <div class="img" v-else></div>
      </div>
    </div>

    <div class="footer">
      <button class="create-btn" :disabled="!canCreate" @click="submitVideo">
        Save
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {defineProps, defineEmits, ref, computed, onMounted, watch} from 'vue';

import * as pkg from 'vue-toastification';
const { useToast } = pkg;
import VueSlider from 'vue-slider-component';

const toast = useToast();

const props = defineProps({
  videoFile: {
    type: File,
    default: null
  },
  videoUrl: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'save']);

const videoSrc = ref('');
const sliderValue = ref([0, 0]); // The range for the start and end points
const duration = ref(0); // Duration in seconds
const canCreate = ref(false);
const muted = ref(true); // State for muted checkbox
const loop = ref(true); // State for loop checkbox
const videoElement = ref<HTMLVideoElement | null>(null); // Reference to the video element

const close = () => {
  emit('close', null);
};

// Watch for video URL changes and fetch video data
watch(loop, () => {
  if (loop) {
    restartAutoplay();
  }
});
// Load the video file and prepare for autoplay
onMounted(() => {
  loadVideo();
  watch(sliderValue, restartAutoplay, {deep: true});
});
// Function to parse the time format "mm:ss" into float (total seconds)
const parseTimeToFloat = (time: string) => {
  const [minutes, seconds] = time.split(':').map(Number);
  return minutes * 60 + seconds; // Convert to total seconds as float
};

// Function to extract query params from URL
const getQueryParams = (url: string) => {
  try {
    const urlParts = url.split('?');
    if (urlParts.length < 2) {
      return { start: 0, end: null };
    }
    
    const params = new URLSearchParams(urlParts[1]);
    const start = params.get('start');
    const end = params.get('end');

    return {
      start: start ? (start.includes(':') ? parseTimeToFloat(start) : parseInt(start, 10)) : 0,
      end: end ? (end.includes(':') ? parseTimeToFloat(end) : parseInt(end, 10)) : null
    };
  } catch (error) {
    console.error('Error parsing query params:', error);
    return { start: 0, end: null };
  }
};

const loadVideo = () => {
  if (props.videoFile) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      videoSrc.value = e.target.result;
      const videoEl = document.createElement('video');
      videoEl.src = videoSrc.value;
      videoEl.onloadedmetadata = () => {
        duration.value = Math.floor(videoEl.duration);
        sliderValue.value = [0, Math.floor(videoEl.duration)]; // Set default slider range
        canCreate.value = true;
        videoEl.muted = true; // Ensure metadata video is muted
        playVideoFromStart(); // Start autoplay once video is loaded
      };
    };
    reader.readAsDataURL(props.videoFile);
  } else if (props.videoUrl !== '') {
    const { start, end } = getQueryParams(props.videoUrl);
    console.log('params', start, end, props.videoUrl);
    videoSrc.value = props.videoUrl;
    const videoEl = document.createElement('video');
    videoEl.src = videoSrc.value;
    videoEl.onloadedmetadata = () => {
      duration.value = Math.floor(videoEl.duration);
      sliderValue.value = [start || 0, end || duration.value]; // Set the slider based on query params
      canCreate.value = true;
      videoEl.muted = true; // Ensure metadata video is muted
      console.log('Local video loaded with start:', start, 'end:', end, 'duration:', duration.value, 'sliderValue:', sliderValue.value);
      restartAutoplay(); // Start autoplay once video is loaded
    };
  }else {
    canCreate.value = false;
  }
};

// Ensure video is always muted
const ensureMuted = () => {
  if (videoElement.value) {
    videoElement.value.muted = true;
  }
};

// Autoplay logic to play the video from the start time
const playVideoFromStart = () => {
  if (videoElement.value) {
    videoElement.value.muted = true; // Ensure muted
    videoElement.value.currentTime = sliderValue.value[0]; // Set to start time
    videoElement.value.play(); // Autoplay
  }
};

// Restart autoplay when start or end changes
const restartAutoplay = () => {
  if (videoElement.value) {
    videoElement.value.muted = true; // Ensure muted
    videoElement.value.currentTime = sliderValue.value[0]; // Reset to new start time
    videoElement.value.play(); // Start playing
  }
};

// Stop video when end time is reached
const checkVideoTime = () => {
  if (videoElement.value && videoElement.value.currentTime >= sliderValue.value[1]) {
    videoElement.value.pause();
  }
};

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

};
const removeQueryParams = (url: string) => {
  return url.split('?')[0]; // This will return the URL without query params
};
const submitVideo = async () => {
  try {
    const baseVideoSrc = removeQueryParams(videoSrc.value); // Remove existing query params
    
    const videoDetails = {
      src: baseVideoSrc,
      start: sliderValue.value[0], // Keep as seconds
      end: sliderValue.value[1],   // Keep as seconds
      file: props.videoFile,
      muted: muted.value,
      loop: loop.value
    };
    
    console.log('EditLocalVideoForm submitting:', videoDetails, 'sliderValue:', sliderValue.value);

    emit('save', videoDetails);
    emit('close');
  } catch (error) {
    toast.error('Failed to save video settings.');
    console.error('Video save error:', error);
  }
};
</script>

<style scoped>
.time > div {
  max-width: 45%;
  flex: 1;
}

.time .start,
.time .end {
  display: flex;
  border: 1px solid #b8b8b8;
  border-radius: 5px;
}

.time .item-title {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 0 5px;
}

.time .item-title input {
  border: unset;
  font-size: 10px;
  padding: 5px;
}

.create-work-form {
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.header h2 {
  font-size: 28px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
}

.content {
  display: flex;
  padding: 0;
}

.form-group {
  max-width: 50%;
}

.form-group:last-child {
  height: 400px;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 14px;
}


.create-btn:enabled {
  background-color: #E3FF00;
  cursor: pointer;
  color: black;
}

.video-preview {
  width: 100%;
  height: 100%;
  border-top-right-radius: 10px;
  object-fit: cover;
}

.time .item-title {
  color: #b8b8b8;
  font-size: 10px;
}

.footer {
  display: flex;
  justify-content: center;
  background: #EDEDED;
  padding: 20px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}
</style>
